'use client';

import {
  useState,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { GroupStage } from './GroupStage';
import { KnockoutStage } from './KnockoutStage';
import {
  Trophy,
  Users,
  AlertTriangle,
  Loader2,
  ArrowLeftRight,
  LayoutGrid,
} from 'lucide-react';
import {
  ForcedReason,
  OverflowGroupClassification,
  TournamentFormat,
  type Fixture,
  type Team,
  type Tournament,
} from '@/types/tournament.model';
import {
  getTournamentById,
  getTournamentFixture,
} from '@/api/tournament/tournamentApi';
import { FixturePending } from './FixturePending';
import { handleGlobalError } from '@/utils/error-handler';
import { ActivateFixtureModal } from './ActivateFixtureModal';
import { CreateFixtureModal } from './CreateFixtureModal';
import { SwapTeamsBetweenGroupsModal } from './SwapTeamsBetweenGroupsModal';
import { SwapTeamsInBracketModal } from './SwapTeamsInBracketModal';
import {
  allGroupMatchesResolved,
  countForcedMatches,
  expectsKnockoutStage,
  getEffectiveClassifiedPerGroup,
  matchCount,
} from '@/utils/fixture-utils';
import {
  isTournamentActive,
  isTournamentCancelled,
  isTournamentFinished,
} from '@/utils/tournament-status';
import { FORCED_REASON_STYLES } from './match/ForcedReasonBadge';
import { SlotOccupancyProvider } from './match/SlotOccupancyDialog';

enum FixtureTab {
  GROUPS = 'groups',
  KNOCKOUT = 'knockout',
}

enum SwapMode {
  GROUPS = 'groups',
  BRACKET = 'bracket',
}

/**
 * The tab a manager should land on when entering the tournament: once the bracket
 * exists and there is no group stage left to play, the tournament IS in its
 * knockout phase, so that is what opens. The groups tab stays enabled either way,
 * one click behind.
 *
 * A finished or cancelled tournament is not "in" its knockout phase any more, so
 * it keeps opening on groups.
 */
function pickDefaultFixtureTab(
  fixture: Fixture | null,
  tournament: Tournament,
): FixtureTab {
  if (isTournamentFinished(tournament) || isTournamentCancelled(tournament)) {
    return FixtureTab.GROUPS;
  }
  if (!fixture?.knockoutTree?.rootNode) return FixtureTab.GROUPS;
  const hasGroupStage = !!fixture.groupList?.length;
  if (hasGroupStage && !allGroupMatchesResolved(fixture)) {
    return FixtureTab.GROUPS;
  }
  return FixtureTab.KNOCKOUT;
}

interface FixtureSectionProps {
  tournamentId: string;
  teams?: Team[]; // Tournament teams
  tournament: Tournament; // Complete tournament to access registrations
  onTournamentUpdated?: (tournament: Tournament) => void; // Callback to update tournament in parent component
}

function FixtureWarning({
  reason,
  children,
}: {
  reason: ForcedReason;
  children: React.ReactNode;
}) {
  return (
    <div
      role="alert"
      className={`flex items-start gap-2 rounded-md border p-2.5 text-xs ${FORCED_REASON_STYLES[reason].banner}`}
    >
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
      <p>{children}</p>
    </div>
  );
}

export function FixtureSection({
  tournamentId,
  teams = [],
  tournament,
  onTournamentUpdated,
}: FixtureSectionProps) {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<FixtureTab>(FixtureTab.GROUPS);
  // Switching tabs keeps the tab bar where it was on screen, so going back and forth
  // never moves the page. `tabsAnchorTop` is the bar's viewport offset at the click.
  const tabsRef = useRef<HTMLDivElement>(null);
  const tabContentRef = useRef<HTMLDivElement>(null);
  const tabsAnchorTop = useRef<number | null>(null);
  // The default tab is decided when the fixture lands, against the tournament as
  // it stands right then. Holding it in a ref keeps the load effect from depending
  // on the tournament object, whose identity changes on every reload.
  const tournamentRef = useRef(tournament);
  tournamentRef.current = tournament;
  const [currentFixture, setCurrentFixture] = useState<Fixture | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingFixture, setIsLoadingFixture] = useState(true);
  const [knockoutError, setKnockoutError] = useState<string | null>(null);
  const [showActivateFixtureModal, setShowActivateFixtureModal] =
    useState(false);
  const [showCreateFixtureModal, setShowCreateFixtureModal] = useState(false);
  const [showSwapTeamsModal, setShowSwapTeamsModal] = useState(false);

  const hasGroupStage = !!currentFixture?.groupList?.length;
  // An exchange needs two teams to trade places; they can sit in different
  // groups or in the same one, so what gates the button is the team count.
  const groupTeamCount = (currentFixture?.groupList ?? []).reduce(
    (total, group) => total + group.teamList.length,
    0,
  );
  // Source of truth for activation: `/fixture/groups` and `/fixture/knockout`
  // (what getTournamentFixture merges into currentFixture) never carry a
  // status field, so it can't be derived from the fixture payload — the
  // tournament's own hasActiveFixture (computed backend-side) survives refetch.
  const isFixtureActive = !!tournament.hasActiveFixture;
  // LONG has no manual activation: the fixture activates on its own when the first round opens.
  const isLong = tournament.format === TournamentFormat.LONG;
  // No group stage means nothing for a groups tab to hold: the fixture is a single view.
  const knockoutOnly = !!tournament.isKnockoutOnly;
  const bracketRoot = currentFixture?.knockoutTree?.rootNode;
  const hasKnockoutStage = !!bracketRoot;
  // Also with an active fixture: the backend only lets through teams that haven't played yet.
  // Without groups the swap moves teams between bracket spots instead.
  let swapMode: SwapMode | null = null;
  if (isTournamentActive(tournament)) {
    if (knockoutOnly) swapMode = bracketRoot ? SwapMode.BRACKET : null;
    else swapMode = groupTeamCount >= 2 ? SwapMode.GROUPS : null;
  }
  const hasFixture = hasGroupStage || hasKnockoutStage;

  const forcedCounts = useMemo(
    () => countForcedMatches(currentFixture),
    [currentFixture],
  );

  // The backend's own threshold (a bracket never takes fewer than 2 teams, even for older
  // knockout-only tournaments saved with a minimum of 1).
  const minTeamsToGenerate =
    tournament.minTeamsToGenerate ?? tournament.minTeamsAllowed;

  // Load fixture data on component mount.
  useEffect(() => {
    const loadFixture = async () => {
      try {
        setIsLoadingFixture(true);
        const fixtureData = await getTournamentFixture(tournamentId);
        setCurrentFixture(fixtureData as Fixture);
        // Batched with the two setters above, so the section paints the right tab
        // on its first commit: no flash of the groups tab, and no wasted mount of
        // the (heavy) group stage tree. This load effect is also the one-shot
        // guarantee the choice needs — `reloadTournament` refetches on its own, so
        // loading a result never re-picks the tab under the manager's feet.
        setActiveTab(
          pickDefaultFixtureTab(fixtureData as Fixture, tournamentRef.current),
        );
      } catch (error) {
        console.error('Error loading fixture:', error);
        // If fixture doesn't exist yet, it's not an error - just no fixture generated
        setCurrentFixture(null);
      } finally {
        setIsLoadingFixture(false);
      }
    };

    // Skip the request entirely when no fixture exists yet: the backend treats
    // "no hay fixture" as a 500, flooding the logs with non-errors.
    if (tournamentId && tournament.hasFixture) {
      loadFixture();
    } else {
      setCurrentFixture(null);
      setIsLoadingFixture(false);
      // Same call, symmetric: with no fixture there is no knockout tab to be on.
      setActiveTab(FixtureTab.GROUPS);
    }
  }, [tournamentId, tournament.hasFixture]);

  const switchTab = (tab: FixtureTab) => {
    if (tab === activeTab) return;
    tabsAnchorTop.current =
      tabsRef.current?.getBoundingClientRect().top ?? null;
    setActiveTab(tab);
  };

  useLayoutEffect(() => {
    const anchorTop = tabsAnchorTop.current;
    const tabs = tabsRef.current;
    const content = tabContentRef.current;
    tabsAnchorTop.current = null;
    if (anchorTop === null || !tabs || !content) return;

    // The padding left by the previous switch goes first, so it never piles up.
    content.style.minHeight = '';
    const top = window.scrollY + tabs.getBoundingClientRect().top - anchorTop;
    // A shorter tab (the bracket next to a long group list) would leave the page too
    // short to hold that scroll, and the browser would clamp it upwards. Pad the
    // content just enough to keep the bar in place.
    const maxTop = document.documentElement.scrollHeight - window.innerHeight;
    if (top > maxTop) {
      content.style.minHeight = `${content.offsetHeight + top - maxTop}px`;
    }
    window.scrollTo({ top });
  }, [activeTab]);

  // Stable identity so the (always mounted) GroupStage memo actually holds.
  const reloadTournament = useCallback(async () => {
    try {
      setIsLoading(true);
      setKnockoutError(null);

      // Get updated fixture from API
      const updatedFixture = await getTournamentFixture(tournamentId);
      setCurrentFixture(updatedFixture as Fixture);

      // Get updated tournament from API for other data
      const updatedTournament = await getTournamentById(tournamentId);

      // Notify parent component about tournament update
      if (onTournamentUpdated) {
        onTournamentUpdated(updatedTournament);
      }
    } catch (error) {
      console.error('Error reloading tournament information:', error);
      handleGlobalError(
        error,
        'Error',
        'Could not load updated tournament information.',
      );
    } finally {
      setIsLoading(false);
    }
  }, [tournamentId, onTournamentUpdated]);

  const handleMatchResultUpdated = useCallback(async () => {
    await reloadTournament();
  }, [reloadTournament]);

  // Combine teams from all groups
  const allTeams = useMemo(() => {
    const teamsMap: Record<string, Team> = {};

    // Add directly provided teams
    teams.forEach((team) => {
      teamsMap[team.id] = team;
    });

    // Add teams from groups if they exist
    currentFixture?.groupList?.forEach((group) => {
      group.teamList.forEach((team) => {
        teamsMap[team.team.id] = team.team;
      });
    });

    return Object.values(teamsMap);
  }, [currentFixture?.groupList, teams]);

  // Render KnockoutStage component with error handling
  const renderKnockoutStage = () => {
    try {
      if (!currentFixture?.knockoutTree?.rootNode) {
        return (
          <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 p-4">
            <p className="text-sm text-amber-700">
              No se puede mostrar la fase de eliminatorias porque no se ha
              generado.
            </p>
          </div>
        );
      }

      return (
        <KnockoutStage
          rootNode={currentFixture.knockoutTree.rootNode}
          tournamentId={tournamentId}
          teams={allTeams}
          onResultUpdated={handleMatchResultUpdated}
          tournamentSportUid={tournament.sport?.uid}
          isFixtureActive={isFixtureActive}
        />
      );
    } catch (error) {
      console.error('Error rendering KnockoutStage:', error);
      setKnockoutError(
        `Error al renderizar la fase de eliminatorias: ${error}`,
      );
      return (
        <div className="rounded-md border border-red-200 bg-red-50 p-4">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-red-500" />
            <h3 className="text-lg font-medium text-red-800">
              Error en la fase de eliminatorias
            </h3>
          </div>
          <p className="mt-2 text-sm text-red-700">
            Ocurrió un error al procesar los datos de la fase de eliminatorias.
          </p>
          <pre className="mt-2 max-h-40 overflow-auto rounded bg-white p-2 text-xs text-red-800">
            {knockoutError}
          </pre>
        </div>
      );
    }
  };

  // Show loading state while fetching fixture
  if (isLoadingFixture) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Trophy className="h-5 w-5" />
            Fixture
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Loading tabs navigation */}
            {!knockoutOnly && (
              <div className="flex w-full rounded-md border p-1">
                <div className="flex flex-1 items-center justify-center gap-2 p-2">
                  <Skeleton className="h-4 w-4 rounded" />
                  <Skeleton className="h-4 w-20" />
                </div>
                <div className="flex flex-1 items-center justify-center gap-2 p-2">
                  <Skeleton className="h-4 w-4 rounded" />
                  <Skeleton className="h-4 w-20" />
                </div>
              </div>
            )}

            {/* Loading content */}
            <div className="mt-4 flex items-center justify-center py-8">
              <div className="flex flex-col items-center gap-3">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                <p className="text-sm text-muted-foreground">
                  Cargando información del fixture...
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  const fixturePending = (
    <FixturePending
      title={knockoutOnly ? 'Cuadro pendiente' : undefined}
      teamsRegistered={teams.length}
      minRequired={minTeamsToGenerate}
      maxAllowed={tournament.maxTeamsAllowed}
      signupDeadlineDate={tournament.signupDeadlineDate}
      onCreateFixture={() => setShowCreateFixtureModal(true)}
    />
  );

  return (
    <SlotOccupancyProvider tournamentId={tournamentId} fixture={currentFixture}>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Trophy className="h-5 w-5" />
            Fixture
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Fixture status and activation (manager view) */}
            {hasFixture && (
              <div className="flex flex-col gap-3 rounded-lg border border-slate-200 bg-slate-50 p-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="space-y-1">
                  <div className="text-sm font-medium text-slate-800">
                    Estado del fixture:{' '}
                    <span className="inline-flex items-center rounded-full bg-slate-800 px-2.5 py-0.5 text-xs font-semibold uppercase tracking-wide text-white">
                      {isFixtureActive ? 'Activo' : 'Borrador'}
                    </span>
                  </div>
                  {!isFixtureActive && !isLong && (
                    <p className="text-xs text-slate-600">
                      Mientras el fixture esté en modo borrador, solo vos lo ves
                      como administrador y no se pueden cargar resultados.
                      Activá el fixture cuando quieras que los jugadores lo vean
                      en la app y para habilitar la carga de resultados.
                    </p>
                  )}
                  {!isFixtureActive && isLong && (
                    <p className="text-xs text-slate-600">
                      El fixture se activa automáticamente cuando abrís la primera
                      jornada desde la sección Fechas.
                    </p>
                  )}

                  {forcedCounts.unscheduled > 0 && (
                    <FixtureWarning reason={ForcedReason.UNSCHEDULED}>
                      <span className="font-semibold">
                        {matchCount(forcedCounts.unscheduled)} sin horario.
                      </span>{' '}
                      {forcedCounts.unscheduled === 1
                        ? 'No había ninguna franja posible, así que quedó con un horario provisional. Buscá el cartel rojo en el fixture y asignale uno:'
                        : 'No había ninguna franja posible, así que quedaron con un horario provisional. Buscá los carteles rojos en el fixture y asignales uno:'}{' '}
                      hasta entonces no vas a poder activar el fixture.
                    </FixtureWarning>
                  )}

                  {forcedCounts.overbooked > 0 && (
                    <FixtureWarning reason={ForcedReason.OVERBOOKED}>
                      <span className="font-semibold">
                        {matchCount(forcedCounts.overbooked)} con cancha
                        solapada.
                      </span>{' '}
                      {forcedCounts.overbooked === 1
                        ? 'Hay más partidos que canchas en su franja.'
                        : 'Hay más partidos que canchas en esas franjas.'}{' '}
                      Esto no bloquea la activación, pero conviene mover alguno
                      a otra franja o revisar que te alcancen las canchas.
                    </FixtureWarning>
                  )}
                </div>

                <div className="flex w-full flex-col gap-2 sm:flex-row sm:justify-end">
                  {swapMode && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="w-full whitespace-nowrap sm:w-auto"
                      onClick={() => setShowSwapTeamsModal(true)}
                      disabled={isLoading}
                    >
                      <ArrowLeftRight className="mr-2 h-4 w-4" />
                      Intercambiar equipos
                    </Button>
                  )}
                  {!isFixtureActive && !isLong && (
                    <Button
                      size="sm"
                      variant="default"
                      className="w-full whitespace-nowrap sm:w-auto"
                      onClick={() => setShowActivateFixtureModal(true)}
                      disabled={isLoading}
                    >
                      Activar fixture
                    </Button>
                  )}
                  {/* The grid works just as well with an active fixture. */}
                  <Button
                    size="sm"
                    variant="outline"
                    className="w-full whitespace-nowrap sm:w-auto"
                    onClick={() => navigate(`/torneos/${tournamentId}/grilla`)}
                    disabled={isLoading}
                  >
                    <LayoutGrid className="mr-2 h-4 w-4" />
                    Ver grilla compartida
                  </Button>
                </div>
              </div>
            )}

            {knockoutOnly ? (
              <div className="mt-4">
                {hasKnockoutStage ? (
                  renderKnockoutStage()
                ) : (
                  fixturePending
                )}
              </div>
            ) : (
              <>
                {/* Custom tabs navigation */}
                <div ref={tabsRef} className="flex w-full rounded-md border p-1">
                  <Button
                    variant={activeTab === FixtureTab.GROUPS ? 'default' : 'ghost'}
                    className="flex flex-1 items-center justify-center gap-2"
                    onClick={() => switchTab(FixtureTab.GROUPS)}
                    disabled={isLoading}
                  >
                    <Users className="h-4 w-4" />
                    Fase de Grupos
                  </Button>
                  <Button
                    variant={
                      activeTab === FixtureTab.KNOCKOUT ? 'default' : 'ghost'
                    }
                    className="flex flex-1 items-center justify-center gap-2"
                    onClick={() => switchTab(FixtureTab.KNOCKOUT)}
                    disabled={!hasKnockoutStage || isLoading}
                  >
                    <Trophy className="h-4 w-4" />
                    Eliminatorias
                  </Button>
                </div>

                {/* Tab content */}
                <div ref={tabContentRef} className="mt-4">
                  {/* Groups stays mounted so GroupStage's expanded/collapsed state
                      survives a tab switch; knockout stays conditional so an
                      unvisited bracket is never rendered. */}
                  <div hidden={activeTab !== FixtureTab.GROUPS}>
                    {hasGroupStage ? (
                      <GroupStage
                        groups={currentFixture?.groupList || []}
                        classifiedPerGroup={getEffectiveClassifiedPerGroup(
                          tournament,
                        )}
                        overflowGroupClassification={
                          tournament.overflowGroupClassification as
                            | OverflowGroupClassification
                            | undefined
                        }
                        format={tournament.format}
                        tournamentId={tournamentId}
                        tournamentSportUid={tournament.sport?.uid}
                        matchDurationMinutes={tournament.matchDuration}
                        registrations={tournament.registrations}
                        tournamentSchedule={tournament.schedule}
                        hasKnockoutStage={hasKnockoutStage}
                        expectsKnockoutStage={expectsKnockoutStage(tournament)}
                        onKnockoutGenerated={reloadTournament}
                        onMatchResultUpdated={handleMatchResultUpdated}
                        isFixtureActive={isFixtureActive}
                      />
                    ) : (
                      fixturePending
                    )}
                  </div>
                  {activeTab === FixtureTab.KNOCKOUT && renderKnockoutStage()}
                </div>
              </>
            )}
          </div>

          <ActivateFixtureModal
            open={showActivateFixtureModal}
            onOpenChange={setShowActivateFixtureModal}
            tournamentId={tournamentId}
            onActivated={reloadTournament}
            knockoutOnly={knockoutOnly}
          />

          <CreateFixtureModal
            open={showCreateFixtureModal}
            onOpenChange={setShowCreateFixtureModal}
            tournamentId={tournamentId}
            tournament={tournament}
            onCreated={reloadTournament}
          />

          {swapMode === SwapMode.GROUPS && currentFixture?.groupList && (
            <SwapTeamsBetweenGroupsModal
              open={showSwapTeamsModal}
              onOpenChange={setShowSwapTeamsModal}
              tournamentId={tournamentId}
              tournamentSportUid={tournament.sport?.uid}
              groups={currentFixture.groupList}
              isFixtureActive={isFixtureActive}
              onSuccess={reloadTournament}
            />
          )}

          {swapMode === SwapMode.BRACKET && bracketRoot && (
            <SwapTeamsInBracketModal
              open={showSwapTeamsModal}
              onOpenChange={setShowSwapTeamsModal}
              tournamentId={tournamentId}
              tournamentSportUid={tournament.sport?.uid}
              rootNode={bracketRoot}
              isFixtureActive={isFixtureActive}
              onSuccess={reloadTournament}
            />
          )}
        </CardContent>
      </Card>
    </SlotOccupancyProvider>
  );
}
